import { DEFAULT_VISIBLE_LAYERS } from '@civilgrid/utils/src/layerConfig/defaultLayers';
import { proxy } from 'valtio';
import { proxySet, subscribeKey } from 'valtio/utils';
import type { AvailableLayer } from '~/api/availableLayers';
import { getPref, Prefs, setPref } from '~/common/prefs';
import type { TOCItem } from '~/common/types';
import {
  getLayerConfig,
  type LayerConfigDocument,
  type SeedLayerConfigBody,
  seedLayerConfig,
} from '../api/layerConfig';
import { getOrgLayerConfig } from '../api/orgLayerConfig';
import { DIGITIZED_SECTION_KEY } from '../pages/layerLibrary/digitizedLibraryGroup';
import { computeLibraryLayerIds } from './computeLibraryLayerIds';
import { documentToState, type LayerConfigState } from './layerConfig.mapping';
import layerStore, { CUSTOM_DATA_SECTION_ID, type LayerStore, PINNED_LAYERS } from './layers';
import { SITE_DATA_LAYER_IDS } from './siteDataLayers';
import { createSupersessionGuard } from './supersessionGuard';

// ---------------------------------------------------------------------------
// Per-user layer config store (the Layer Library).
//
// Owns load/failure states, the seed cutover, hydration of the shared LayerStore from the
// backend document, the Library membership actions, and the debounced PUT sync. The shared layer
// state itself stays in LayerStore — this store only owns the persistence/hydration
// *policy* plus the document-membership bookkeeping, and drives LayerStore through
// its public surface.
// ---------------------------------------------------------------------------

/**
 * Load state of the backend document.
 * `idle` = no active organization (`deactivate`) — picker edits mutate the shared LayerStore directly.
 */
export type LayerConfigLoadState = 'idle' | 'loading' | 'loaded' | 'failed';

/**
 * Top-level Utilities section node id — the one section the first-use seed
 * default-expands (the picker should open showing rows, not a wall
 * of collapsed sections). Hardcoded like `TOP_LEVEL_CATEGORY_ORDER`'s 'Utilities'.
 */
const UTILITIES_SECTION_NODE_ID = '_toc:Utilities';

/**
 * Document fields the picker does not edit, retained verbatim from the hydrated
 * document so the write-back reproduces it exactly: the leaf-id membership
 * (including ids the current catalog can't place — they must survive the round-trip
 * untouched, so a transient catalog gap can never delete a user's layer) and the
 * server's stale-leaf GC stamps (echoed back uninterpreted).
 */
interface LayerConfigMeta {
  /** Every document leaf id, in document order. */
  nodeIds: string[];
  /** id → server-written GC stamp, serialized back untouched. */
  missingSince: Map<string, string>;
}

/** Every leaf id under a TOC subtree, in tree order. */
function collectTocLeafIds(items: readonly TOCItem[]): string[] {
  return items.flatMap((item) => ('children' in item ? collectTocLeafIds(item.children) : [item.id]));
}

/** Every nested parent's sectionId in a runtime TOC group. */
function collectTocSectionIds(items: readonly TOCItem[]): string[] {
  return items.flatMap((item) =>
    'children' in item ? [...(item.sectionId ? [item.sectionId] : []), ...collectTocSectionIds(item.children)] : [],
  );
}

/**
 * Expansion keys of the split-upload parent rows in the Custom Data overlay group (CG-957) —
 * `customData:<rowId>`. Derived from the group rather than by prefix-matching `expandedSections`,
 * so only rows that actually exist are carried and no arbitrary key can ride along.
 */
function collectCustomDataUploadSectionIds(items: readonly TOCItem[]): string[] {
  return items.flatMap((item) => ('children' in item && item.sectionId ? [item.sectionId] : []));
}

export class LayerConfigStore {
  /** loading → loaded / failed. */
  loadState: LayerConfigLoadState = 'loading';
  /**
   * Whether the debounced sync is allowed to PUT. Armed ONLY after a successful
   * hydrate (a `200` GET or seed) — the no-overwrite invariant's decision point. Never
   * armed on a load failure, so a failed/uncertain read can't cascade into an overwrite.
   */
  canPersist = false;
  /** Non-edited document fields retained for a lossless write-back; `null` until first hydrate. */
  meta: LayerConfigMeta | null = null;
  /**
   * The catalog **group** ids "in the user's library" — the grain the Library page works at.
   * A projection over the leaf-only document via the any-present predicate: a group is in
   * the library iff ANY of its current catalog leaves is a document entry (consistent with
   * the digitized-squash rule). Rebuilt on hydrate / catalog arrival / add / remove. Drives
   * the catalog table's available-rows filter and the Selected Layers rail.
   */
  libraryLayerIds: Set<string> = proxySet<string>();
  /**
   * The active org's default leaf-id set, for the Reset fallback + dialog wording.
   * `undefined` = not yet loaded (Reset disabled), `null` = org has no defaults (→ CivilGrid
   * fallback), `string[]` = the org's raw default leaf ids (catalog-filtered at use). Reactive.
   */
  orgDefaultLeafIds: string[] | null | undefined = undefined;
  /**
   * Bumped whenever the catalog (and the node-id resolution paths derived from it) change —
   * a cheap reactive dependency for the picker's section→node-id resolution, since
   * the catalog itself is private.
   */
  catalogVersion = 0;

  /**
   * The doc-store's OWN editable state. The picker edits these — never the
   * shared `layerStore` proxies — and `visibleLeafIds`/`pinnedLeafIds` are the sole source
   * the debounced sync observes. `layerStore.visibleLayerIds` / `pinnedLayerIds` /
   * `expandedSections` are a RENDER MIRROR of these (leaf-only); every non-picker
   * writer of the shared sets (download/export/data-request/headless/side-panels) lands in
   * the shared sets only, so it affects the session map but can never reach the document.
   * `visibleLeafIds` is leaf-only — each parent's `visible` is derived from its descendant
   * leaves at serialization, never stored. `expandedNodeIds` is DEVICE-LOCAL state: persisted to
   * localStorage (`Prefs.EXPANDED_NODE_IDS`; no value → collapsed), never to the document —
   * the sync does not watch it and serialization never reads it.
   */
  visibleLeafIds: Set<string> = proxySet<string>();
  pinnedLeafIds: Set<string> = proxySet<string>();
  expandedNodeIds: Set<string> = proxySet<string>();

  /**
   * In-memory pre-isolate snapshot backing the picker's one-level "Undo". Captures both
   * visibility sets — the isolate also wholesale-clears the shared render set's
   * render-only overlays, which the doc set alone can't restore. Replaced by each
   * isolate, dropped by any other visibility edit and by hydrate/deactivate; never
   * serialized to the document.
   */
  isolationUndo: { leafId: string; previousVisibleLeafIds: string[]; previousRenderVisibleIds: string[] } | null = null;

  // The org the latest `bootstrap` is resolving for; a response for any other org is stale and
  // ignored (guards the org-switch race — the React trigger re-bootstraps on org change).
  private activeOrganizationId: string | null = null;

  // Monotonic per-`bootstrap` token. Each call captures the value it incremented to; a resume
  // after any `await` proceeds only while it is still the latest. This supersedes an org-id check
  // alone — a newer **same-org** bootstrap (e.g. a retry) also wins.
  private readonly bootstrapGuard = createSupersessionGuard();

  // Lifecycle callback slots the sync controller registers into, so the store never
  // imports the sync module (no import cycle; the store stays free of sync machinery).
  // Plain Sets of functions — valtio leaves Sets/functions unproxied, and these are not
  // reactive state.
  private hydrateListeners = new Set<(document: LayerConfigDocument, organizationId: string | null) => void>();

  // Group-grain catalog rows keyed by group node id, set by the page once the
  // `useAvailableLayers` query resolves. The structural authority the Library actions
  // join against (ancestor chains, leaf children); `null` until the catalog arrives.
  private catalog: Map<string, AvailableLayer> | null = null;

  // leaf id → its root-first parent node-id path [...ancestor keys, group key], derived
  // from the catalog. Classifies leaf-vs-parent (a node is a leaf iff it is a key here),
  // drives the picker's section→node-id resolution, and derives parent `visible` at write.
  // Empty until the catalog arrives.
  private leafParentPaths = new Map<string, string[]>();

  // Every parent node id the current catalog knows (ancestor sections + group keys) — the
  // id universe picker expansion lives in. Gates which ids `setExpanded` persists and prunes
  // stale ids out of the expansion pref at hydrate. Empty until the catalog arrives.
  private catalogParentIds = new Set<string>();

  // Catalog leaves unverified users must not access: every leaf rooted under the Utilities
  // section (which includes the digitized branch — `_toc:Utilities / Digitized Layers`) plus
  // every special-dataset leaf (org-custom uploads are special datasets, proprietary by
  // definition). Derived structurally from node ids/scope, never from display labels, so a
  // GIS label rename can't silently disarm the guard. Consumers re-read on `catalogVersion`.
  private protectedLeafIds = new Set<string>();

  // Block-hydrate-on-catalog: the leaf-only render mirror + parent-visible derivation
  // need `leafParentPaths`, supplied asynchronously by `setCatalog`. `bootstrap` awaits this
  // before hydrating so the catalog is guaranteed loaded at hydrate time.
  private catalogReady = false;
  // The org the loaded catalog (and the `leafParentPaths` derived from it) belongs to. A
  // bootstrap for a different org must wait for ITS catalog rather than resolving against the
  // previous org's stale paths — the catalog is org-scoped (`useAvailableLayers` keys by org).
  // `null` = org-agnostic (tests / legacy callers that don't supply an org), which matches any.
  private catalogOrganizationId: string | null = null;
  private catalogReadyResolvers: Array<{ organizationId: string; resolve: () => void }> = [];

  // Set when `hydrate()` skips the mirror because a download form is paused. Consumed
  // by the constructor's `pausePersistence` watcher once the pause lifts, so the document is
  // never stranded behind a stale pre-hydrate render snapshot until reload.
  private pendingMirror = false;

  // The shared layer state this store drives (the render target). Injectable for tests;
  // defaults to the singleton.
  constructor(private store: LayerStore = layerStore) {
    // `reset()` flips `pausePersistence` false and restores its snapshot synchronously;
    // this batched watcher fires after, so the deferred mirror cleanly overwrites the stale
    // snapshot (the document is the source of truth). Re-checks live state — a rapid
    // re-pause before this fires must leave the mirror pending for the next flip.
    subscribeKey(this.store, 'pausePersistence', () => {
      if (!this.pendingMirror || this.loadState !== 'loaded' || this.store.pausePersistence) return;
      this.pendingMirror = false;
      this.mirrorAllToLayerStore();
    });
  }

  /**
   * First-use cutover for `organizationId` (triggered by `useLayerConfigBootstrap`). Stops
   * localStorage persistence for the feature keys (the DB document is the source of truth), then
   * resolves the document:
   * - `found` → `hydrate`;
   * - `absent` (the server's in-body `not_seeded` — definitively no row) → one-time
   *   **non-destructive** read of the legacy localStorage keys
   *   (visibility + pins), `POST /seed`, then `hydrate` the seeded document;
   * - `5xx` / seed failure → `markFailed` (persistence never armed).
   *
   * Safe to call again as a retry / on org switch: a newer call supersedes via the bootstrap token,
   * and `canPersist` stays false until a successful hydrate, so a failed read never arms a PUT. No
   * reset — the live layers stay as the user left them (and localStorage stays intact) until hydrate.
   */
  async bootstrap(organizationId: string) {
    const token = this.bootstrapGuard.next();
    this.activeOrganizationId = organizationId;
    this.orgDefaultLeafIds = undefined; // "loading" for this bootstrap
    // Stop writing the legacy localStorage keys. Reads (the seed migration below) are
    // unaffected; getPref does not consult this.
    this.store.suppressLocalPersistence = true;
    this.loadState = 'loading';
    this.canPersist = false;
    // An org switch mid-download must not later mirror the PREVIOUS org's document once
    // this bootstrap's own hydrate (or none at all) resolves.
    this.pendingMirror = false;

    const result = await getLayerConfig(organizationId);
    if (!this.bootstrapGuard.isCurrent(token)) return; // superseded by a newer bootstrap

    if (result.status === 'error') {
      this.markFailed();
      return;
    }

    // Block hydrate on the catalog: the leaf-only render mirror and the parent-visible
    // derivation both need `leafParentPaths`, supplied asynchronously by `setCatalog`.
    await this.whenCatalogReady(organizationId);
    if (!this.bootstrapGuard.isCurrent(token)) return; // superseded by a newer bootstrap

    if (result.status === 'found') {
      // Fail closed if hydrate throws on a malformed stored document (older shape): surface the
      // failure + retry rather than stranding `loading` forever.
      try {
        this.hydrate(result.document);
      } catch {
        this.markFailed();
        return;
      }
    } else {
      // `absent` (the server's in-body not_seeded) — first use. Pull the user's current localStorage state
      // (visibility + pins) and seed from it, then hydrate. localStorage is intact here: nothing resets it
      // and nothing writes the legacy keys, so the read is the user's real selection.
      try {
        const seeded = await seedLayerConfig(organizationId, readLegacyMigrationBody());
        if (!this.bootstrapGuard.isCurrent(token)) return; // superseded by a newer bootstrap
        this.seedDefaultExpansionPref();
        this.hydrate(seeded);
      } catch {
        if (!this.bootstrapGuard.isCurrent(token)) return;
        this.markFailed();
        return;
      }
    }

    // Load the org's default set for the Reset fallback. After hydrate (loadState is
    // already 'loaded', so first paint isn't blocked); its own guard handles an org switch and it
    // never markFailed's, so a denied/absent org read can't break the user's own config load.
    await this.loadOrgDefaults(organizationId);
  }

  /**
   * Fetch the active org's default leaf set for the Reset fallback + dialog wording.
   * Read-only; independent of the user hydrate — never `markFailed` (the user's own config must
   * load regardless). Guarded by `activeOrganizationId`, so a response arriving after an org
   * switch is ignored. Also called by the page on Reset-dialog open to refresh a stale set — on
   * a transient error there, `??=` preserves an already-loaded set rather than clobbering it to
   * null (only the very first, still-`undefined` load fails safe to null).
   */
  async loadOrgDefaults(organizationId: string) {
    try {
      const result = await getOrgLayerConfig(organizationId);
      if (this.activeOrganizationId !== organizationId) return; // superseded by an org switch
      if (result.status === 'found') {
        this.orgDefaultLeafIds = Object.keys(result.document.nodes);
      } else if (result.status === 'not_seeded') {
        this.orgDefaultLeafIds = null; // org has no defaults → CivilGrid floor
      } else {
        // 'error': getOrgLayerConfig resolves (never rejects) its error paths, so keep an
        // already-loaded set on a transient blip; only fall to CivilGrid if none was ever loaded.
        this.orgDefaultLeafIds ??= null;
      }
    } catch {
      // Defensive: getOrgLayerConfig resolves its errors, so this only covers an unexpected
      // synchronous throw. Preserve any already-loaded set.
      if (this.activeOrganizationId !== organizationId) return;
      this.orgDefaultLeafIds ??= null;
    }
  }

  /**
   * Resolve once the catalog for `organizationId` has arrived (`setCatalog` ran for that org,
   * or org-agnostically). `bootstrap` awaits this before hydrating so it never hydrates
   * against the *previous* org's `leafParentPaths` after an org switch. If the matching catalog
   * is already present this resolves synchronously. The catalog load is an app-level
   * prerequisite; a never-resolving catalog leaves the picker in `loading` (canPersist stays
   * false — no overwrite risk).
   */
  private whenCatalogReady(organizationId: string): Promise<void> {
    if (this.catalogReady && (this.catalogOrganizationId === null || this.catalogOrganizationId === organizationId)) {
      return Promise.resolve();
    }
    return new Promise((resolve) => {
      this.catalogReadyResolvers.push({ organizationId, resolve });
    });
  }

  /**
   * Hydrate the shared LayerStore from the backend document and arm persistence. The single
   * document → store entry point (the swap point for a future bootstrap-delivered snapshot).
   * Sets the load state to `loaded` and arms `canPersist` — the debounced PUT
   * may run only after this.
   */
  hydrate(document: LayerConfigDocument) {
    const state = documentToState(document);

    this.isolationUndo = null;
    this.store.suppressLocalPersistence = true;
    this.meta = { nodeIds: state.nodeIds, missingSince: state.missingSince };

    // Populate the doc-store's OWN editable sets (the persist source), then mirror them into
    // the shared `layerStore` for render. `visibleLeafIds` is leaf-only; parent visibility
    // is derived from descendant leaves at serialization, not stored here.
    this.replaceSet(this.visibleLeafIds, state.visibleLeafIds);
    this.replaceSet(this.pinnedLeafIds, state.pinnedLeafIds);
    // Expansion is device-local: read from localStorage — never from the document —
    // pruning ids the current catalog has no parent node for. No value → all collapsed.
    this.replaceSet(this.expandedNodeIds, this.readExpandedNodeIdsPref());
    this.persistExpandedNodeIds(); // write the pruned set back so the pref never accretes stale ids
    // A mounted download form owns `layerStore`'s visibility/pins/expansion for its own
    // picker — a hydrate landing mid-download-mode (deep-link + late catalog resolution)
    // must not stomp it. The doc-store's own sets above are still populated so persistence stays
    // correct; only the render mirror is deferred (`pendingMirror`) until the pause lifts (see
    // the constructor's `pausePersistence` watcher).
    if (this.store.pausePersistence) {
      this.pendingMirror = true;
    } else {
      this.pendingMirror = false;
      this.mirrorAllToLayerStore();
    }

    this.rebuildLibraryLayerIds();
    this.loadState = 'loaded';
    this.canPersist = true;

    // Fire AFTER persistence is armed so the sync controller baselines against the exact
    // document just hydrated (a fresh document never inherits a pending write).
    this.hydrateListeners.forEach((listener) => {
      listener(document, this.activeOrganizationId);
    });
  }

  /**
   * Mark the backend load as failed (`5xx` / network / malformed doc). `canPersist` stays false, so a
   * failed read can never cascade into an overwriting PUT (the no-overwrite invariant). No reset —
   * the live layers stay as the user left them; the picker surfaces a retry.
   */
  markFailed() {
    this.loadState = 'failed';
    this.canPersist = false;
  }

  /**
   * Surface a catalog-load failure (`GET /v1/availableLayers` errored — non-member 403,
   * 5xx, network) as a bootstrap failure. `bootstrap` blocks hydrate on the catalog
   * (`whenCatalogReady`); without this the awaited promise never resolves and the picker sits
   * on a **silent infinite loader** instead of the `failed` retry banner. The catalog is owned by
   * react-query (`useLayerConfigBootstrap`), which reports the error in here; the retry affordance
   * refetches it.
   *
   * Guarded: only fails the **active** org's still-`loading` bootstrap — never clobbers a `loaded`
   * state or a superseded org. Idempotent and re-applicable, so a retry that fails again re-fails
   * (rather than regressing to the silent loader).
   */
  markCatalogUnavailable(organizationId: string | null | undefined) {
    if (organizationId !== this.activeOrganizationId) return;
    if (this.loadState !== 'loading') return;
    this.markFailed();
  }

  /**
   * Disarm the document store when there is no active organization. Every picker handler
   * branches on `loadState === 'loaded'`, so after a successful hydrate this MUST run or the
   * handlers keep editing the document store — and the sync can PUT those stale edits addressed
   * to the last hydrated org. Disarms persistence (`canPersist` false: scheduled and future
   * flushes no-op) and supersedes any in-flight bootstrap via the token, so a late-resolving
   * hydrate cannot re-arm. Live layer state and localStorage stay untouched (the same no-reset
   * stance as `markFailed`).
   */
  deactivate() {
    this.bootstrapGuard.invalidate();
    this.activeOrganizationId = null;
    this.loadState = 'idle';
    this.canPersist = false;
    this.pendingMirror = false; // a deactivate mid-download must not later mirror a stale doc
    this.isolationUndo = null;
  }

  /**
   * Register a sync-lifecycle listener fired after every successful hydrate, with the hydrated
   * document and the org it belongs to. Returns an unsubscribe function.
   */
  onHydrate(listener: (document: LayerConfigDocument, organizationId: string | null) => void): () => void {
    this.hydrateListeners.add(listener);
    return () => {
      this.hydrateListeners.delete(listener);
    };
  }

  /**
   * Current layer-config state as a mapping-module `LayerConfigState`, or `null` before the
   * first hydrate. The read seam the sync's dirty-diff / `PUT` serialization consume; pairs with
   * `stateToDocument`. Returns snapshot copies of the live sets so callers can diff safely.
   */
  getState(): LayerConfigState | null {
    if (!this.meta) return null;
    return {
      nodeIds: [...this.meta.nodeIds],
      // The doc-store's OWN editable sets — NOT the shared `layerStore`: render-only
      // writers of the shared sets never reach serialization. Snapshot copies so callers
      // diff safely.
      visibleLeafIds: new Set(this.visibleLeafIds),
      pinnedLeafIds: new Set(this.pinnedLeafIds),
      missingSince: this.meta.missingSince,
    };
  }

  /**
   * Supply the group-grain catalog (the `useAvailableLayers` result) the Library actions
   * join against. Idempotent; call whenever the query (re)resolves — e.g. on org switch. Pass
   * the org the catalog belongs to so a bootstrap for that org unblocks on ITS catalog (and a
   * still-pending bootstrap for a *different* org keeps waiting); omit it for org-agnostic
   * callers (tests), where it matches any waiter.
   */
  setCatalog(layers: readonly AvailableLayer[], organizationId: string | null = null) {
    this.catalog = new Map(layers.map((layer) => [layer.key, layer]));
    this.leafParentPaths = new Map();
    this.catalogParentIds = new Set();
    this.protectedLeafIds = new Set();
    for (const layer of layers) {
      const path = [...layer.ancestors.map((ancestor) => ancestor.key), layer.key];
      for (const id of path) this.catalogParentIds.add(id);
      // Root = first ancestor, or the group itself when top-level.
      const rootNodeId = layer.ancestors[0]?.key ?? layer.key;
      const isProtected =
        rootNodeId === UTILITIES_SECTION_NODE_ID || layer.scope === 'special' || layer.specialDatasetSlug != null;
      for (const subLayer of layer.subLayers) {
        this.leafParentPaths.set(subLayer.key, path);
        if (isProtected) this.protectedLeafIds.add(subLayer.key);
      }
    }
    this.catalogVersion += 1;
    this.rebuildLibraryLayerIds();

    // Release every bootstrap whose org this catalog satisfies (block-hydrate-on-catalog);
    // an org-agnostic catalog (`null`) satisfies all. Waiters for a different org stay pending.
    this.catalogReady = true;
    this.catalogOrganizationId = organizationId;
    const pending: Array<{ organizationId: string; resolve: () => void }> = [];
    for (const waiter of this.catalogReadyResolvers) {
      if (organizationId === null || organizationId === waiter.organizationId) waiter.resolve();
      else pending.push(waiter);
    }
    this.catalogReadyResolvers = pending;
  }

  /**
   * True when `leafId` is a layer unverified users must not access (see the
   * `protectedLeafIds` derivation in {@link setCatalog}). False until the catalog arrives —
   * callers that need coverage during that window OR this with the code-side id sets
   * (`isProtectedLayerId` in `store2/protectedLayers.ts` does exactly that).
   */
  isProtectedLeafId(leafId: string): boolean {
    return this.protectedLeafIds.has(leafId);
  }

  /**
   * True when the current catalog can place `leafId` in the picker tree — i.e. the leaf is
   * document-manageable and render-mirrorable. False until the catalog arrives, and false
   * for render-only overlay leaves the catalog does not offer; callers with such leaves
   * (the Environmental / GeoTech tab toggles) fall back to direct render-set writes.
   */
  hasCatalogLeaf(leafId: string): boolean {
    return this.leafParentPaths.has(leafId);
  }

  /**
   * Build the picker tree from the document + catalog: one leaf per document
   * entry — and ONLY document entries; a leaf the document does not mention is not shown —
   * with sections/groups, labels, and structure derived entirely from the catalog at
   * render time (the leaf-only document stores nothing structural, so rebucketing or a
   * bucket rename upstream re-renders the user's leaves in their new positions with no
   * document change). A parent renders iff some document leaf lives under it.
   *
   * Siblings sort by flattened catalog position — the catalog is the single ordering
   * authority (nothing ordering-related is stored). Parents carry their node id as
   * `sectionId`, so the picker's expansion keys need no label resolution.
   *
   * **Render resilience:** a document leaf the catalog has no node for during a
   * transient upstream gap is retained as a top-level row with its id as the fallback
   * label, never silently erased. A leaf the server stamped `missingSince` (confirmed
   * absent from a healthy catalog, awaiting GC) is NOT rendered — the entry survives in
   * the document, but a removed layer's raw id must not surface in the picker.
   *
   * Returns `null` until both the document and the catalog have loaded — the picker falls
   * back to the static TOC for that (brief, load-state-bannered) window.
   */
  buildDocumentTOC(): TOCItem[] | null {
    if (!this.meta || !this.catalog) return null;
    const present = new Set(this.meta.nodeIds);

    // Re-assemble the all-depth node graph from the group-grain catalog rows: every
    // ancestor section, group, and leaf, with label + parent + stable position.
    type GraphNode = { id: string; label: string; parentId: string | null; isLeaf: boolean; position: number };
    const graph = new Map<string, GraphNode>();
    let position = 0;
    for (const layer of this.catalog.values()) {
      let parentId: string | null = null;
      for (const ancestor of layer.ancestors) {
        if (!graph.has(ancestor.key)) {
          graph.set(ancestor.key, {
            id: ancestor.key,
            label: ancestor.label,
            parentId,
            isLeaf: false,
            position: position++,
          });
        }
        parentId = ancestor.key;
      }
      if (!graph.has(layer.key)) {
        graph.set(layer.key, { id: layer.key, label: layer.label, parentId, isLeaf: false, position: position++ });
      }
      for (const subLayer of layer.subLayers) {
        if (!graph.has(subLayer.key)) {
          graph.set(subLayer.key, {
            id: subLayer.key,
            label: subLayer.label,
            parentId: layer.key,
            isLeaf: true,
            position: position++,
          });
        }
      }
    }

    const childrenOf = new Map<string | null, GraphNode[]>();
    for (const node of graph.values()) {
      // Leaves render iff the document mentions them; parents are structural and enter
      // unconditionally — build() prunes any parent with no document leaf beneath it.
      if (node.isLeaf && !present.has(node.id)) continue;
      const siblings = childrenOf.get(node.parentId) ?? [];
      siblings.push(node);
      childrenOf.set(node.parentId, siblings);
    }
    const sortSiblings = (siblings: GraphNode[]) => siblings.sort((a, b) => a.position - b.position);

    const build = (node: GraphNode): TOCItem | null => {
      if (node.isLeaf) return { id: node.id, label: node.label };
      const children = sortSiblings(childrenOf.get(node.id) ?? [])
        .map(build)
        .filter((child): child is TOCItem => child !== null);
      if (children.length === 0) return null;
      return { label: node.label, expanded: false, children, sectionId: node.id };
    };

    const tree = sortSiblings(childrenOf.get(null) ?? [])
      .map(build)
      .filter((item): item is TOCItem => item !== null);

    // Retain document leaves the catalog can't place (id as the fallback label) rather
    // than silently erasing a user's layer during a catalog gap. Leaves the server has
    // stamped `missingSince` are the exception: the stamp means "absent from a HEALTHY
    // catalog" (confirmed removed, inside the GC retention window), not a transient gap,
    // so they stay out of the picker — while still riding through serialization untouched.
    const missingSince = this.meta.missingSince;
    const unplaceable = this.meta.nodeIds.filter((id) => !graph.has(id) && !missingSince.has(id));
    return [...tree, ...unplaceable.map((id) => ({ id, label: id }))];
  }

  /**
   * Whether `nodeId` is a parent node in the current catalog
   */
  isCatalogParent(nodeId: string): boolean {
    return this.catalogParentIds.has(nodeId);
  }

  /**
   * Resolve a picker parent (a static-TOC section/group, which carries only a label) to its
   * document node id via its descendant leaf ids: the DEEPEST node common to every known
   * leaf's catalog parent path. A direct parent of leaves resolves to its group key;
   * a top section (leaves spread across groups) to the shared section key. Returns `null`
   * when no leaf is known to the catalog (e.g. project-scoped or special-dataset parents) —
   * the picker then falls back to its legacy label key, which serialization drops,
   * so unresolvable parents keep session-only expansion instead of corrupting the document.
   */
  resolveParentNodeId(descendantLeafIds: readonly string[]): string | null {
    const paths: string[][] = [];
    for (const leafId of descendantLeafIds) {
      const path = this.leafParentPaths.get(leafId);
      if (path) paths.push(path);
    }
    if (paths.length === 0) return null;

    const [first] = paths;
    for (let depth = first.length - 1; depth >= 0; depth--) {
      const candidate = first[depth];
      if (paths.every((path) => path.includes(candidate))) return candidate;
    }
    return null;
  }

  /**
   * Add a Library group to the user's document: create an entry for every
   * **current catalog leaf** of the group that the document lacks, picker checkbox OFF.
   * Leaf-grain by design — no group/ancestor entry is ever written (structure is
   * the catalog's job at render). On a partially-present group this **fills the gaps**
   * (the any-present membership already counts it as "added"); already-present leaves
   * keep their state untouched. No-op when every leaf is present, or until both the
   * document and the catalog have loaded.
   */
  addToLibrary(groupNodeId: string) {
    const layer = this.catalog?.get(groupNodeId);
    if (!layer || !this.meta) return;

    const present = new Set(this.meta.nodeIds);
    const missingLeafIds = layer.subLayers.map((subLayer) => subLayer.key).filter((id) => !present.has(id));
    if (missingLeafIds.length === 0) return; // fully present — nothing to fill

    // Membership only — no visibility writes, so an isolate-undo snapshot stays valid.
    for (const leafId of missingLeafIds) {
      this.meta.nodeIds.push(leafId);
    }

    // The added group's ancestors open up so its row is immediately visible in the picker;
    // the group itself stays collapsed, and sibling nodes' expansion is untouched (set-add only).
    // Device-local: persisted to the expansion pref, not the document.
    for (const id of layer.ancestors.map((ancestor) => ancestor.key)) {
      this.expandedNodeIds.add(id);
      this.store.expandedSections.add(id);
    }
    this.persistExpandedNodeIds();

    this.rebuildLibraryLayerIds();
  }

  /**
   * Remove a Library group from the user's document: clear every **present**
   * leaf of the group's current catalog leaf set — the exact inverse of the any-present
   * membership (the group stops counting as "added" the moment its last leaf goes).
   * Removed ids are cleared from the editable sets and render mirrors; the group's own
   * expansion collapses (ancestors keep theirs — other groups may still render under
   * them, and a childless parent isn't rendered anyway). No group/ancestor document
   * entries exist to prune. No-op until both the document and the catalog have loaded.
   */
  removeFromLibrary(groupNodeId: string) {
    const layer = this.catalog?.get(groupNodeId);
    if (!layer || !this.meta) return;

    const present = new Set(this.meta.nodeIds);
    const removeIds = new Set(layer.subLayers.map((subLayer) => subLayer.key).filter((id) => present.has(id)));
    if (removeIds.size === 0) return; // not in the library

    this.isolationUndo = null;
    this.meta.nodeIds = this.meta.nodeIds.filter((id) => !removeIds.has(id));
    removeIds.forEach((id) => {
      this.meta?.missingSince.delete(id);
      // Clear from the doc-store's editable sets (the persist source)…
      this.visibleLeafIds.delete(id);
      this.pinnedLeafIds.delete(id);
      // …and mirror the removal into the shared render sets.
      this.store.visibleLayerIds.delete(id);
      this.store.removePinnedLayer(id);
    });
    this.expandedNodeIds.delete(groupNodeId);
    this.store.expandedSections.delete(groupNodeId);
    this.persistExpandedNodeIds();

    this.rebuildLibraryLayerIds();
  }

  /**
   * The membership bucket of a catalog leaf, from its `leafParentPaths` chain: the
   * Digitized Layers section id when the leaf sits anywhere under that section (the
   * Library's squash grain), else the leaf's owning group — the chain's last node.
   * Mirrors the server reconciler's `membershipBucketOf` (`@civilgrid/utils`
   * `userLayerConfig.ts`; duplicated by design — the client does not depend on
   * `@civilgrid/utils`). Same grain on both sides is what makes a reset document a
   * fixed point of load-path reconciliation.
   */
  private membershipBucketOf(path: readonly string[]): string {
    return path.includes(DIGITIZED_SECTION_KEY) ? DIGITIZED_SECTION_KEY : path[path.length - 1];
  }

  /**
   * Build a reset target from `anchorLeafIds` at the membership-bucket grain:
   * membership = every catalog leaf whose bucket contains a (catalog-known) anchor, in
   * catalog order (`leafParentPaths` preserves it); visible = the anchors alone, so
   * bucket siblings land present-but-unchecked — the same shape the server reconciler
   * injects, so reconciliation finds nothing to add to a fresh reset. A bucket with no
   * anchor contributes nothing (adding a whole bucket is the user's explicit Library
   * "Add"). `roots` — the included leaves' top-level sections — drives only the
   * post-reset picker expansion. `null` until the catalog has loaded, or when no anchor
   * is catalog-known (the caller treats that as "no usable target").
   */
  private bucketExpandedTarget(
    anchorLeafIds: Iterable<string>,
  ): { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null {
    if (this.leafParentPaths.size === 0) return null;

    const anchors = new Set<string>();
    const buckets = new Set<string>();
    for (const leafId of anchorLeafIds) {
      const path = this.leafParentPaths.get(leafId);
      if (!path) continue; // not catalog-known
      anchors.add(leafId);
      buckets.add(this.membershipBucketOf(path));
    }
    if (anchors.size === 0) return null;

    const roots = new Set<string>();
    const nodeIds: string[] = [];
    const visibleLeafIds = new Set<string>();
    for (const [leafId, path] of this.leafParentPaths) {
      if (!buckets.has(this.membershipBucketOf(path))) continue;
      const root = path[0];
      if (root) roots.add(root);
      nodeIds.push(leafId);
      if (anchors.has(leafId)) visibleLeafIds.add(leafId);
    }
    return { roots, nodeIds, visibleLeafIds };
  }

  /**
   * The defaults-only document target `resetToDefaultLayers` produces:
   * {@link bucketExpandedTarget} anchored on `DEFAULT_VISIBLE_LAYERS`. `null` until the
   * catalog has loaded (or if no default is catalog-known — reset no-ops rather than
   * emptying the document). Shared by the reset itself and `isAtDefaultLayers` so the
   * two can never disagree about what "defaults" means.
   */
  private defaultLayersTarget(): { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null {
    return this.bucketExpandedTarget(DEFAULT_VISIBLE_LAYERS);
  }

  /**
   * The target built from the org's default leaf set —
   * {@link bucketExpandedTarget} anchored on the org's leaves: bucket siblings join the
   * membership unchecked, only the org's own leaves visible. Returns null when the org
   * has no usable defaults (loading, none, or zero overlap with this user's catalog) →
   * caller uses CivilGrid.
   */
  private orgDefaultTarget(): { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null {
    if (!this.orgDefaultLeafIds || this.orgDefaultLeafIds.length === 0) return null;
    return this.bucketExpandedTarget(this.orgDefaultLeafIds);
  }

  /**
   * Guarantee the Site Data leaves in a defaults target. Site Data is not
   * library-curated — the Library hides its group and the server's load-path
   * reconciliation always injects its leaves (visible) into every document — so a reset
   * target must include them too, or a reset to org defaults that lack Site Data would
   * drop the section until the next load re-injects it (and `isAtDefaultLayers` would
   * disagree with the server-converged document). Catalog-known leaves only; mutates the
   * freshly-built target in place.
   */
  private withGuaranteedSiteData(
    target: { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null,
  ): { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null {
    if (!target) return null;
    const present = new Set(target.nodeIds);
    for (const leafId of Object.values(SITE_DATA_LAYER_IDS)) {
      const path = this.leafParentPaths.get(leafId);
      if (!path) continue; // not in this catalog — nothing to guarantee
      if (!present.has(leafId)) target.nodeIds.push(leafId);
      target.visibleLeafIds.add(leafId);
      const root = path[0];
      if (root) target.roots.add(root);
    }
    return target;
  }

  /** The effective defaults target — the org's set if usable, else CivilGrid's. */
  private effectiveDefaultTarget(): { roots: Set<string>; nodeIds: string[]; visibleLeafIds: Set<string> } | null {
    return this.withGuaranteedSiteData(this.orgDefaultTarget() ?? this.defaultLayersTarget());
  }

  /**
   * Does the user's Reset fall back to the ORG's defaults (vs CivilGrid's)? Drives the
   * reset-dialog wording and the `fallback_target` analytics property. Reads `orgDefaultLeafIds`
   * (reactive) so the page can depend on it.
   */
  hasOrgDefaults(): boolean {
    return this.orgDefaultTarget() !== null;
  }

  /**
   * Whether the current document's MEMBERSHIP already matches the defaults
   * `resetToDefaultLayers` would produce (order-insensitive). Drives the Library's Reset
   * Layers disabled state — disabled doubles as the "you're at defaults" indicator.
   * Membership only, matching what the Library page displays: visibility and pins are
   * picker-side state with no Library row to explain a deviation (Site Data especially —
   * its group is hidden from the Library entirely), so they must not enable the
   * destructive Reset with no visible cause. Expansion is device-local UI state, never
   * compared. `false` while the document or catalog is still loading (reset no-ops there
   * too, and the button is already disabled pre-load via the edit gate). Matches the org
   * store's membership-only check (`OrgLayerConfigStore.isAtDefaultLayers`).
   */
  isAtDefaultLayers(): boolean {
    if (!this.meta) return false;
    const target = this.effectiveDefaultTarget();
    if (!target) return false;

    const memberIds = new Set(this.meta.nodeIds);
    return memberIds.size === target.nodeIds.length && target.nodeIds.every((id) => memberIds.has(id));
  }

  /**
   * Reset the user's layer config to the effective defaults (the Library's destructive
   * "Reset Layers" action): document membership becomes what a fresh defaults-only seed
   * produces — every catalog leaf of every membership bucket a default leaf lives in
   * (bucket-grain pull-in; org defaults when the org has them, else CivilGrid's), with
   * only the default leaves visible, pins cleared, and only the covered top-level
   * sections expanded (device-local). The debounced sync persists it like any edit
   * (whole document, last-write-wins). No-op until both the document and the catalog
   * have loaded.
   */
  resetToDefaultLayers() {
    if (!this.meta) return;
    const target = this.effectiveDefaultTarget();
    if (!target) return;

    this.isolationUndo = null;
    this.visibleLeafIds.clear();
    this.pinnedLeafIds.clear();
    this.expandedNodeIds.clear();

    for (const leafId of target.visibleLeafIds) this.visibleLeafIds.add(leafId);
    for (const root of target.roots) this.expandedNodeIds.add(root);

    this.meta = { nodeIds: target.nodeIds, missingSince: new Map() };
    this.persistExpandedNodeIds();
    this.rebuildLibraryLayerIds();
    // Full render reset to match the new defaults document.
    this.mirrorAllToLayerStore();
  }

  // -------------------------------------------------------------------------
  // Picker edit facade. The shared picker handlers (handlers.ts) call these
  // UNCONDITIONALLY; each branches once, internally, on the mode:
  //   - not loaded (loading / failed / idle) → mutate the shared `layerStore` directly;
  //   - loaded → mutate the doc-store's OWN editable set + mirror into `layerStore`.
  // This is what guarantees the no-overwrite invariant: the persist source (the doc-store sets)
  // is touched only by the picker, never by download/export/side-panel writers of `layerStore`.
  // -------------------------------------------------------------------------

  /** Toggle a leaf's picker checkbox. Leaf-only — section toggles go through `setSectionVisible`. */
  setLayerVisible(leafId: string, visible: boolean) {
    this.isolationUndo = null;
    if (this.loadState !== 'loaded') {
      // Not loaded: mutate the shared set directly.
      // A tab-locked leaf keeps rendering — the active tab owns its render entry.
      if (visible) this.store.visibleLayerIds.add(leafId);
      else if (!this.store.lockedLeafIds.has(leafId)) this.store.visibleLayerIds.delete(leafId);
      return;
    }
    this.setLeafVisible(leafId, visible);
  }

  /**
   * Bulk-toggle a set of leaves (a section/preset). `leafIds` is already flattened to leaves by
   * the caller (`collectLeafIds`). Backend-managed: only document leaves are persisted; an
   * off-document leaf in the set is dropped (document membership is the picker's boundary),
   * but still mirrored for the session render.
   */
  setSectionVisible(leafIds: readonly string[], visible: boolean) {
    this.isolationUndo = null;
    if (this.loadState !== 'loaded') {
      for (const leafId of leafIds) {
        if (visible) this.store.visibleLayerIds.add(leafId);
        else if (!this.store.lockedLeafIds.has(leafId)) this.store.visibleLayerIds.delete(leafId);
      }
      return;
    }
    for (const leafId of leafIds) this.setLeafVisible(leafId, visible);
  }

  /** Turn off every layer (the picker's "turn off all"). */
  turnOffAll() {
    this.isolationUndo = null;
    if (this.loadState !== 'loaded') {
      this.store.toggleAllLayers(false);
      return;
    }
    this.visibleLeafIds.clear();
    // "All off" clears the shared render set wholesale (incl. any render-only overlays and
    // linked-org visibility), matching the legacy UX; the doc-store set is now empty too.
    this.store.toggleAllLayers(false);
  }

  /**
   * Turn on every picker layer (the global menu's "Turn On All Layers"). Leaves the picker
   * hides (`hiddenLeafIds`: protected in unverified mode, project-scoped outside a project,
   * stood-aside or empty Site Data) are skipped, so a checkbox the user can't see never gets
   * `visible: true` persisted behind it.
   */
  turnOnAll(hiddenLeafIds?: ReadonlySet<string>) {
    this.isolationUndo = null;
    if (this.loadState !== 'loaded') {
      this.store.toggleAllLayers(true);
      return;
    }
    // Every document leaf turns visible; only catalog-known leaves mirror to the render set.
    for (const leafId of this.meta?.nodeIds ?? []) {
      if (hiddenLeafIds?.has(leafId)) continue;
      this.setLeafVisible(leafId, true);
    }
  }

  /**
   * Show only one leaf (the picker's "Only" isolate). Backend-managed: the doc-store set
   * becomes exactly `{leafId}` (a persist-path edit, like a checkbox toggle — legacy isolate
   * persisted too, via the localStorage subscription on the shared set), then the legacy
   * isolate mirrors it into the shared render set — the wholesale clear (incl. render-only
   * overlays and linked-org visibility) and deferred re-add match the legacy UX. Like
   * `setLeafVisible`, a leaf the catalog can't place (a retained fallback row) updates the
   * persist source but is never mirrored — the render set only clears.
   */
  isolateLayer(leafId: string) {
    if (this.loadState !== 'loaded') {
      this.store.isolateLayer(leafId);
      return;
    }
    this.isolationUndo = {
      leafId,
      previousVisibleLeafIds: [...this.visibleLeafIds],
      previousRenderVisibleIds: [...this.store.visibleLayerIds],
    };
    this.visibleLeafIds.clear();
    this.visibleLeafIds.add(leafId);
    if (this.leafParentPaths.has(leafId)) {
      this.store.isolateLayer(leafId);
    } else {
      this.store.toggleAllLayers(false);
    }
  }

  /**
   * Restore the visibility state captured by the last isolate (the isolated row's "Undo").
   * One-level: consumes the snapshot. Both sets are restored in place — the doc-store edit
   * persists through the debounced sync like any picker edit — and tab-locked ids are
   * re-asserted into the render set.
   */
  undoIsolate() {
    const undo = this.isolationUndo;
    if (!undo || this.loadState !== 'loaded') return;
    // Copy to plain arrays before nulling the reactive field.
    const previousVisibleLeafIds = [...undo.previousVisibleLeafIds];
    const previousRenderVisibleIds = [...undo.previousRenderVisibleIds];
    this.isolationUndo = null;
    this.replaceSet(this.visibleLeafIds, previousVisibleLeafIds);
    this.replaceSet(this.store.visibleLayerIds, previousRenderVisibleIds);
    this.store.lockedLeafIds.forEach((id) => {
      this.store.visibleLayerIds.add(id);
    });
  }

  /** Bookmark / un-bookmark a leaf (picker pin). `isPinned` is the state before the toggle. */
  togglePin(leafId: string, isPinned: boolean) {
    if (this.loadState !== 'loaded') {
      if (isPinned) this.store.removePinnedLayer(leafId);
      else this.store.addPinnedLayer(leafId);
      return;
    }
    if (isPinned) {
      this.pinnedLeafIds.delete(leafId);
      this.store.removePinnedLayer(leafId);
    } else {
      this.pinnedLeafIds.add(leafId);
      this.store.pinnedLayerIds.add(leafId);
    }
  }

  /**
   * Expand/collapse a picker parent. Backend-managed: expansion is device-local — a catalog
   * parent node id updates `expandedNodeIds` and persists to localStorage
   * (`Prefs.EXPANDED_NODE_IDS`), never to the document; an unresolvable parent
   * (project-scoped / special-dataset label key) or the render-only Bookmarked group expands
   * session-only in the shared set.
   */
  setExpanded(nodeId: string, expanded: boolean) {
    if (this.loadState !== 'loaded') {
      if (expanded) this.store.expandedSections.add(nodeId);
      else this.store.expandedSections.delete(nodeId);
      return;
    }
    if (this.catalogParentIds.has(nodeId)) {
      if (expanded) this.expandedNodeIds.add(nodeId);
      else this.expandedNodeIds.delete(nodeId);
      this.persistExpandedNodeIds();
    }
    // Mirror for render either way (a session-only expansion of an unresolvable parent lives
    // only in the shared set and is never persisted).
    if (expanded) this.store.expandedSections.add(nodeId);
    else this.store.expandedSections.delete(nodeId);
  }

  /**
   * Expand / collapse every picker section (the global "Expand/Collapse All Sections").
   * Expansion is device-local — this method never touches the document. With the
   * document loaded, the document only supplies which sections exist:
   * `expandedNodeIds` becomes the document tree's full resolvable parent set (every parent on
   * a document leaf's `leafParentPaths` chain — exactly the parents `buildDocumentTOC`
   * renders) or empty, persisted to the localStorage pref (`Prefs.EXPANDED_NODE_IDS`). The
   * shared render set mirrors it wholesale; the synthetic Bookmarked group and the linked-orgs
   * section follow session-only, matching the legacy toggle.
   */
  setAllExpanded(expanded: boolean) {
    if (this.loadState !== 'loaded') {
      this.store.toggleAllSections(expanded);
      return;
    }
    this.expandedNodeIds.clear();
    if (expanded) {
      for (const leafId of this.meta?.nodeIds ?? []) {
        const parentPath = this.leafParentPaths.get(leafId);
        if (parentPath) for (const parentId of parentPath) this.expandedNodeIds.add(parentId);
      }
    }
    this.persistExpandedNodeIds();

    this.store.expandedSections.clear();
    this.expandedNodeIds.forEach((id) => {
      this.store.expandedSections.add(id);
    });
    if (expanded) this.store.expandedSections.add(PINNED_LAYERS);
    // The project-scoped Custom Data overlay section (CustomDataTOC) follows session-only,
    // like the Bookmarked group — never the localStorage pref or the document. Its split-upload
    // parent rows are sections too, so expand-all must reach them (the clear() above collapses
    // them on expand-all=false).
    if (expanded && this.store.customDataTOCGroup.length > 0) {
      this.store.expandedSections.add(CUSTOM_DATA_SECTION_ID);
      for (const sectionId of collectCustomDataUploadSectionIds(this.store.customDataTOCGroup)) {
        this.store.expandedSections.add(sectionId);
      }
    }
    this.store.linkedOrgsSectionExpanded = expanded;
  }

  /**
   * Backend-managed single-leaf visibility edit: update the doc-store's own set (the persist
   * source) and mirror the one leaf into the shared render set. Touching a single id keeps any
   * render-only overlay leaves in `layerStore.visibleLayerIds` untouched. A document
   * leaf the catalog can't place updates the persist source only — there is nothing renderable
   * to mirror, but the edit still round-trips to the record.
   */
  private setLeafVisible(leafId: string, visible: boolean) {
    if (visible) {
      this.visibleLeafIds.add(leafId);
      if (this.leafParentPaths.has(leafId)) this.store.visibleLayerIds.add(leafId);
    } else {
      this.visibleLeafIds.delete(leafId);
      // A tab-locked leaf keeps rendering: the document edit still persists the user's
      // intent, but the active tab owns the render-set entry until it unlocks.
      if (!this.store.lockedLeafIds.has(leafId)) this.store.visibleLayerIds.delete(leafId);
    }
  }

  /** Replace a proxied set's contents in place (clear + add) without swapping the proxy. */
  private replaceSet(target: Set<string>, source: Iterable<string>) {
    target.clear();
    for (const id of source) target.add(id);
  }

  /**
   * Read the device-local expansion pref (`Prefs.EXPANDED_NODE_IDS`, a JSON array of parent
   * node ids), pruned to the parents the current catalog actually has — a renamed/rebucketed
   * parent's stale id silently drops. No value (or a malformed one) → collapsed (empty).
   */
  private readExpandedNodeIdsPref(): string[] {
    const ids = parseJsonArrayPref(getPref(Prefs.EXPANDED_NODE_IDS));
    if (!this.catalog) return ids; // no catalog to prune against (never the bootstrap path — hydrate is catalog-blocked)
    return ids.filter((id) => this.catalogParentIds.has(id));
  }

  /** Persist the expansion set to its device-local pref (never to the document). */
  private persistExpandedNodeIds() {
    setPref(Prefs.EXPANDED_NODE_IDS, JSON.stringify([...this.expandedNodeIds]));
  }

  /**
   * First-use default: Utilities starts expanded so the picker opens showing rows.
   * Runs on the seed path only, writing the device-local pref BEFORE hydrate so the id
   * flows the normal read→prune→mirror→persist pipeline (including the deferred render
   * mirror). Later loads read only the pref, so a user's subsequent collapse wins.
   * No-op when this catalog has no Utilities section.
   */
  private seedDefaultExpansionPref() {
    if (!this.catalogParentIds.has(UTILITIES_SECTION_NODE_ID)) return;
    const ids = new Set(parseJsonArrayPref(getPref(Prefs.EXPANDED_NODE_IDS)));
    ids.add(UTILITIES_SECTION_NODE_ID);
    setPref(Prefs.EXPANDED_NODE_IDS, JSON.stringify([...ids]));
  }

  /**
   * Full-reset mirror of the doc-store's own editable sets into the shared `layerStore` for
   * render — the single writer of the shared sets in backend-managed mode.
   * Catalog-known leaves only:
   * `visibleLayerIds` never receives a parent id OR a document id the catalog can't place
   * (a retained-but-unrenderable leaf must not reach `MapLayers` and raise "missing config"
   * error reports — and the analytics payload stays leaf-grain). Used on hydrate / reset (a
   * clean slate); incremental edits mirror their own touched ids to preserve session-only
   * render overlays.
   */
  private mirrorAllToLayerStore() {
    // The project-scoped Custom Data overlay (CustomDataTOC) is render state the document owns
    // nothing of — CustomDataLayers decides it from the project's own localStorage record. A full
    // reset would silently wipe that whenever a hydrate lands after that decision (org
    // re-bootstrap, a slow document GET), so carry the overlay ids' current visibility and the
    // section's expansion across the reset.
    // The group nests since CG-957: a multi-category shapefile row is a TOCParent whose per-category
    // children are the ids that actually drive visibility, so carry LEAF ids, not top-level ids.
    const customDataIds = new Set(collectTocLeafIds(this.store.customDataTOCGroup));
    const visibleCustomDataIds = [...this.store.visibleLayerIds].filter((id) => customDataIds.has(id));
    // Split-upload parent rows are sections of their own, so their expansion needs the same
    // carry-over as the group's own section key — otherwise a hydrate collapses an open row.
    const expandedCustomDataSectionIds = [
      CUSTOM_DATA_SECTION_ID,
      ...collectCustomDataUploadSectionIds(this.store.customDataTOCGroup),
    ].filter((id) => this.store.expandedSections.has(id));

    // Geo-referenced maps are the same kind of project-scoped overlay: carry their visibility and expansion too.
    const georeferencedMapIds = new Set(collectTocLeafIds(this.store.georeferencedMapsTOCGroup));
    const visibleGeoreferencedMapIds = [...this.store.visibleLayerIds].filter((id) => georeferencedMapIds.has(id));
    const expandedGeoreferencedMapSectionIds = collectTocSectionIds(this.store.georeferencedMapsTOCGroup).filter((id) =>
      this.store.expandedSections.has(id),
    );

    this.replaceSet(this.store.visibleLayerIds, [
      ...[...this.visibleLeafIds].filter((id) => this.leafParentPaths.has(id)),
      ...visibleCustomDataIds,
      ...visibleGeoreferencedMapIds,
    ]);
    // Tab-locked ids survive the wholesale rebuild (same guard as toggleAllLayers): the
    // active tab owns their render entries until it unlocks them, so a hydrate or defaults
    // reset landing mid-tab must not wipe the layers the tab is forcing on.
    this.store.lockedLeafIds.forEach((id) => {
      this.store.visibleLayerIds.add(id);
    });

    this.store.pinnedLayerIds.clear();
    this.store.pinnedLayers.clear();
    this.pinnedLeafIds.forEach((id) => {
      if (this.leafParentPaths.has(id)) this.store.addPinnedLayer(id);
    });

    this.store.expandedSections.clear();
    this.expandedNodeIds.forEach((id) => {
      this.store.expandedSections.add(id);
    });
    // The synthetic Bookmarked-layers group is a render-time grouping, never a document node
    // — default it expanded each session (collapse is session-only; the id is not in
    // `nodeIds`, so serialization drops it and it can never reach the document).
    this.store.expandedSections.add(PINNED_LAYERS);
    for (const sectionId of expandedCustomDataSectionIds) this.store.expandedSections.add(sectionId);
    for (const sectionId of expandedGeoreferencedMapSectionIds) this.store.expandedSections.add(sectionId);
  }

  /**
   * Recompute the group-grain Library projection via the any-present predicate: a catalog
   * group is "in the library" iff ANY of its current catalog leaves is a document entry.
   */
  private rebuildLibraryLayerIds() {
    this.libraryLayerIds.clear();
    if (!this.meta || !this.catalog) return;
    for (const id of computeLibraryLayerIds(this.catalog, this.meta.nodeIds)) this.libraryLayerIds.add(id);
  }
}

/** Split a comma-joined pref (the `PREFERRED_MAP_LAYERS` / `LAST_USED_LAYERS` CSV format) into ids, dropping empties. */
function parseCsvPref(raw: string | null): string[] {
  if (!raw) return [];
  return raw.split(',').filter(Boolean);
}

/** Parse a JSON-array pref (`PINNED_LAYER_IDS` / `EXPANDED_SECTIONS`), tolerating malformed values. */
function parseJsonArrayPref(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Read the legacy localStorage keys into a seed body. One-time, **non-destructive**
 * migration input: the keys are read, never written or deleted. After seeding, the feature never touches localStorage again. Ids
 * the org cannot have are dropped server-side, not here.
 *
 * Only two dimensions migrate: **visibility** ← the per-map `PREFERRED_MAP_LAYERS.homeMap` key (the
 * layers the user saw on the home map), and **pins** ← the global `PINNED_LAYER_IDS`. Expansion is
 * not part of the seed — it is device-local (`EXPANDED_NODE_IDS`), never the document.
 */
function readLegacyMigrationBody(): SeedLayerConfigBody {
  return {
    lastUsedLayerIds: parseCsvPref(getPref(Prefs.PREFERRED_MAP_LAYERS, 'homeMap')),
    pinnedLayerIds: parseJsonArrayPref(getPref(Prefs.PINNED_LAYER_IDS)),
  };
}

const layerConfigStore = proxy(new LayerConfigStore());

/**
 * Whether the picker may be edited for a given load state. Only `loaded` is editable;
 * `loading` / `failed` are read-only (the picker shows a loader / retry). UX only — the
 * no-overwrite invariant is enforced by `canPersist`, not by disabling controls.
 */
export function canEditLayerConfig(loadState: LayerConfigLoadState): boolean {
  return loadState === 'loaded';
}

/** Whether the picker shows its loader: while the document loads, and while no organization is active yet. */
export function isLayerConfigPending(loadState: LayerConfigLoadState): boolean {
  return loadState === 'loading' || loadState === 'idle';
}

export default layerConfigStore;
